import { PendingResendsWidget } from '@/components/updateResend/PendingResendsWidget';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, ClipboardCheck, Briefcase, TrendingUp, FileText, ArrowRight, Settings, GripVertical, Eye, EyeOff, X, Plus, Search, AlertCircle, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import MarketingRecapCard from '@/components/MarketingRecapCard';
import ServiceOptionMixWidget from '@/components/ServiceOptionMixWidget';
import RecentUploadsWidget from '@/components/RecentUploadsWidget';
import EmailModal from '@/pages/EmailModal';
import { FindClientOrPropertyDialog } from '@/components/FindClientOrProperty';

const ALL_WIDGETS = [
  { id: 'kpis', label: 'KPI Cards', desc: 'Clients, appraisals, active jobs, leads' },
  { id: 'pipeline', label: 'Job Pipeline', desc: 'Bar chart of appraisal statuses' },
  { id: 'recent_appraisals', label: 'Recent Appraisals', desc: 'Latest appraisal activity' },
  { id: 'contract_value', label: 'Contract Value', desc: 'Total contract value' },
  { id: 'overdue_invoices', label: 'Overdue Invoices', desc: 'Invoices unpaid 10+ days — chase list' },
  { id: 'financial_summary', label: 'Financial Summary', desc: 'Revenue & profit overview' },
  { id: 'low_stock', label: 'Low Stock Alerts', desc: 'Inventory items below reorder level' },
  { id: 'upcoming_followups', label: 'Upcoming Follow-ups', desc: 'Telemarketing follow-up reminders' },
  { id: 'recent_activity', label: 'Audit Activity', desc: 'Recent system actions' },
  { id: 'marketing_recap', label: 'Marketing Recap', desc: 'Weekly recap email — schedule + send-now controls' },
  { id: 'cultural_calendar', label: 'Cultural Calendar', desc: 'Upcoming culturally-significant dates for your active client base' },
  { id: 'service_mix', label: 'Service Mix (6m)', desc: 'Reroof vs New Roof vs Repairs vs Restoration — monthly stacked bar' },
  { id: 'recent_uploads', label: 'Recently Uploaded', desc: 'Last 5 documents uploaded — scans pending OCR are flagged' },
];

const DEFAULT_WIDGETS = ['kpis', 'pipeline', 'service_mix', 'recent_appraisals', 'recent_uploads', 'overdue_invoices', 'contract_value', 'marketing_recap', 'cultural_calendar'];

export default function DashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [overdue, setOverdue] = useState(null);
  // Upcoming culturally-significant dates for the office's active client
  // base. Widget renders only when the cultural_calendar widget is on
  // AND there's at least one item in the horizon.
  const [culturalUpcoming, setCulturalUpcoming] = useState([]);
  // Per-client nudges within each celebration's reminder window — drives
  // the "Send greeting" one-tap buttons. Loaded alongside upcoming.
  const [culturalNudges, setCulturalNudges] = useState([]);
  const [greetingModal, setGreetingModal] = useState(null);  // { to, subject, body, client_id, ... }
  const [loading, setLoading] = useState(true);
  const [showCustomize, setShowCustomize] = useState(false);
  const [tempWidgets, setTempWidgets] = useState([]);
  const [showQuickAppraisal, setShowQuickAppraisal] = useState(false);

  // Widgets introduced AFTER existing users had saved prefs. These get
  // auto-merged into the user's active list so the widget appears the
  // first time they load the dashboard — they can still disable via
  // Customize. Add new widget IDs here when shipping new widgets.
  const NEW_WIDGETS_AUTO_ADD = ['service_mix', 'recent_uploads'];

  const activeWidgets = useMemo(() => {
    const base = prefs?.widgets || DEFAULT_WIDGETS;
    // De-dupe + ensure every NEW_WIDGETS_AUTO_ADD entry is present.
    const merged = [...base];
    for (const w of NEW_WIDGETS_AUTO_ADD) {
      if (!merged.includes(w)) merged.push(w);
    }
    return merged;
  }, [prefs]);

  const load = useCallback(async () => {
    try {
      const statsRes = await api.get('/dashboard/stats');
      setStats(statsRes.data);
      const [prefsRes, finRes, stockRes, auditRes, overdueRes, culturalRes, nudgesRes] = await Promise.all([
        api.get('/dashboard/preferences').catch(() => ({ data: { widgets: DEFAULT_WIDGETS } })),
        api.get('/financial/overview?period=month').catch(() => ({ data: null })),
        api.get('/inventory?low_stock=true').catch(() => ({ data: [] })),
        api.get('/audit-log?limit=8').catch(() => ({ data: { items: [] } })),
        api.get('/invoices/overdue?days=10').catch(() => ({ data: null })),
        api.get('/cultural-toolkit/upcoming?days=45').catch(() => ({ data: { items: [] } })),
        api.get('/cultural-toolkit/nudges?days=14').catch(() => ({ data: { items: [] } })),
      ]);
      setPrefs(prefsRes.data);
      setFinancials(finRes.data);
      setLowStock(stockRes.data || []);
      setAuditLog(auditRes.data?.items || []);
      setOverdue(overdueRes.data);
      setCulturalUpcoming(culturalRes.data?.items || []);
      setCulturalNudges(nudgesRes.data?.items || []);
    } catch (e) {
      console.error('Dashboard load error:', e);
      if (e.response?.status === 401) {
        window.location.href = '/login';
        return;
      }
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const savePrefs = async (widgets) => {
    setPrefs(prev => ({ ...prev, widgets }));
    await api.put('/dashboard/preferences', { widgets });
    setShowCustomize(false);
  };

  const moveWidget = (fromIdx, toIdx) => {
    const arr = [...tempWidgets];
    const [item] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, item);
    setTempWidgets(arr);
  };

  const toggleWidget = (id) => {
    setTempWidgets(prev => prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id]);
  };


  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Loading dashboard...</div></div>;

  const pipeline = stats?.pipeline_counts || {};
  // Keep "Appraisal Booked" and "Work Booked" distinct on the chart
  // (both used to collapse to just "Booked"). Same for the two
  // "Completed" buckets (one is `Appraisal Completed`, the other is
  // the post-job `Completed`). Use concise prefix tags to disambiguate.
  const LABEL_REWRITES = {
    'Client Data':         'Client Data',
    'Quote Sent':          'Quoting',
    'Appraisal Required':  'Appr. Required',
    'Appraisal Booked':    'Appr. Booked',
    'Appraisal Completed': 'Appr. Completed',
    'Work Booked':         'Work Booked',
    'Work In Progress':    'In Progress',
    'Completed':           'Job Completed',
  };
  const chartData = Object.entries(pipeline).map(([name, value]) => ({
    name: LABEL_REWRITES[name] || name,
    value,
  }));
  const barColors = ['#D35400', '#E67E22', '#F39C12', '#2F855A', '#4A6FA5', '#3182CE', '#D69E2E', '#805AD5', '#38A169'];

  // Robust status pill mapping covering legacy + canonical Access statuses
  const statusPill = (status) => {
    const s = (status || '').toLowerCase();
    if (!s) return 'bg-slate-100 text-slate-500';
    if (s.includes('paid')) return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100';
    if (s.includes('completed') || s.includes('complete')) return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100';
    if (s.includes('invoiced')) return 'bg-teal-50 text-teal-700 ring-1 ring-teal-100';
    if (s.includes('accepted') || s.includes('work booked') || s === 'work in progress' || s.includes('progress')) return 'bg-blue-50 text-blue-700 ring-1 ring-blue-100';
    if (s.includes('quoted') || s.includes('quote') || s.includes('follow up')) return 'bg-amber-50 text-amber-700 ring-1 ring-amber-100';
    if (s.includes('booked') || s.includes('required')) return 'bg-orange-50 text-orange-700 ring-1 ring-orange-100';
    if (s.includes('declined') || s.includes('cancelled') || s.includes('lost') || s.includes('written') || s.includes('overdue')) return 'bg-rose-50 text-rose-700 ring-1 ring-rose-100';
    if (s.includes('closed')) return 'bg-slate-100 text-slate-600 ring-1 ring-slate-200';
    return 'bg-slate-100 text-slate-600';
  };

  const totalPipelineCount = Object.values(pipeline).reduce((a, b) => a + (b || 0), 0);

  const kpis = [
    { label: 'Total Clients', value: stats?.clients_count || 0, icon: Users, color: '#4A6FA5', tint: 'bg-blue-50' },
    { label: stats?.job_model_enabled ? 'Jobs' : 'Appraisals', value: stats?.appraisals_count || 0, icon: ClipboardCheck, color: '#D35400', tint: 'bg-orange-50' },
    ...(stats?.job_model_enabled ? [{ label: `Quotes · ${stats?.quotes_sent || 0} sent`, value: stats?.quotes_count || 0, icon: ClipboardCheck, color: '#7C3AED', tint: 'bg-violet-50' }] : []),
    { label: 'Active Jobs', value: stats?.active_jobs || 0, icon: Briefcase, color: '#2F855A', tint: 'bg-emerald-50' },
    { label: 'Open Leads', value: stats?.open_leads || 0, icon: TrendingUp, color: '#D69E2E', tint: 'bg-amber-50' },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Overview of your roofing business</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="dashboard-start-workflow-btn" onClick={() => window.location.assign('/workflow/start')} className="flex items-center gap-1.5 px-3.5 py-2 bg-sky-600 text-white rounded-md text-xs font-semibold hover:bg-sky-700 hover:shadow-md hover:shadow-sky-500/20 transition-all"><Plus size={14} strokeWidth={2.5} /> Start Workflow</button>
          <button data-testid="quick-new-appraisal-btn" onClick={() => setShowQuickAppraisal(true)} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#D35400] text-white rounded-md text-xs font-semibold hover:bg-[#A04000] hover:shadow-md hover:shadow-orange-500/20 transition-all"><Plus size={14} strokeWidth={2.5} /> New Appraisal</button>
          <button data-testid="customize-dashboard-button" onClick={() => { setTempWidgets([...activeWidgets]); setShowCustomize(true); }} className="flex items-center gap-2 px-3 py-2 border border-[#E2E2DF] rounded-md text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"><Settings size={14} /> Customize</button>
        </div>
      </div>
      <PendingResendsWidget />

      <FindClientOrPropertyDialog open={showQuickAppraisal} onClose={() => setShowQuickAppraisal(false)} mode="go" />

      {/* KPIs */}
      {activeWidgets.includes('kpis') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map(kpi => (
            <div key={kpi.label} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`} className="relative bg-white border border-[#E2E2DF] rounded-md p-5 card-lift cursor-pointer overflow-hidden" onClick={() => navigate(kpi.label === 'Total Clients' ? '/clients' : kpi.label === 'Appraisals' ? '/appraisals' : kpi.label === 'Open Leads' ? '/leads' : '/appraisals?status=Work+In+Progress')}>
              <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: kpi.color }} />
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">{kpi.label}</span>
                <div className={`w-9 h-9 rounded-md ${kpi.tint} flex items-center justify-center`}>
                  <kpi.icon size={18} style={{ color: kpi.color }} strokeWidth={1.75} />
                </div>
              </div>
              <span className="text-[2.25rem] leading-none font-extrabold text-slate-900 font-[Manrope] font-mono-num tracking-tight">{kpi.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Chart */}
        {activeWidgets.includes('pipeline') && (
          <div className="lg:col-span-2 bg-white border border-[#E2E2DF] rounded-md p-5">
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="text-base font-semibold text-slate-800 font-[Manrope]">Job Pipeline</h3>
              <span className="text-xs text-slate-500 font-mono-num">{totalPipelineCount.toLocaleString()} jobs</span>
            </div>
            <p className="text-xs text-slate-400 mb-4">Distribution across canonical statuses</p>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 50 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5A5D63' }} angle={-35} textAnchor="end" interval={0} axisLine={{ stroke: '#E2E2DF' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#5A5D63' }} allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #E2E2DF', borderRadius: 6, boxShadow: '0 4px 12px rgba(15,23,42,0.06)' }} cursor={{ fill: 'rgba(211,84,0,0.04)' }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{chartData.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Recent Appraisals */}
        {activeWidgets.includes('recent_appraisals') && (
          <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-800 font-[Manrope]">Recent Appraisals</h3>
              <button onClick={() => navigate('/appraisals')} className="text-[#D35400] hover:text-[#A04000] text-xs font-semibold flex items-center gap-1 transition-colors">View All <ArrowRight size={14} /></button>
            </div>
            <div className="space-y-1">
              {(stats?.recent_appraisals || []).slice(0, 6).map(a => (
                <div key={a._id} className="flex items-center justify-between gap-3 py-2.5 border-b border-[#E2E2DF] last:border-0 cursor-pointer hover:bg-slate-50 px-2 -mx-2 rounded transition-colors" onClick={() => navigate(`/appraisals/${a._id}`)}>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${a.client_name ? 'text-slate-800' : 'text-slate-400 italic'}`}>{a.client_name || 'Draft (no client linked)'}</p>
                    <p className="text-xs text-slate-500 truncate">{a.quote_type_ref || a.service_option || 'No service option'}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${statusPill(a.appraisal_status)}`}>{a.appraisal_status || 'New'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Contract Value */}
      {activeWidgets.includes('contract_value') && stats?.total_contract_value > 0 && (
        <div className="relative bg-gradient-to-br from-white to-emerald-50/30 border border-[#E2E2DF] rounded-md p-6 overflow-hidden">
          <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-emerald-100/40 blur-2xl" />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center ring-1 ring-emerald-100">
              <FileText size={22} className="text-[#2F855A]" strokeWidth={1.75} />
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">Total Contract Value</span>
              <p className="text-3xl font-extrabold text-slate-900 font-[Manrope] font-mono-num tracking-tight mt-0.5">${(stats.total_contract_value || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
          </div>
        </div>
      )}

      {/* Service Mix (Phase 2-4 workflow capacity-planning widget) */}
      {activeWidgets.includes('service_mix') && (
        <ServiceOptionMixWidget api={api} />
      )}

      {/* Recently Uploaded — daily nudge of new scans / docs across the
          system. Surfaces scans pending OCR (Phase D placeholder). */}
      {activeWidgets.includes('recent_uploads') && (
        <RecentUploadsWidget api={api} />
      )}

      {/* Overdue Invoices */}
      {activeWidgets.includes('overdue_invoices') && overdue && (
        <div data-testid="overdue-invoice-widget" className={`relative border rounded-md overflow-hidden ${overdue.count > 0 ? 'bg-gradient-to-br from-rose-50/40 to-white border-rose-200' : 'bg-gradient-to-br from-emerald-50/30 to-white border-emerald-100'}`}>
          <div className={`absolute inset-x-0 top-0 h-[3px] ${overdue.count > 0 ? 'bg-rose-500' : 'bg-emerald-500'}`} />
          <div className="p-5">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-md flex items-center justify-center ring-1 ${overdue.count > 0 ? 'bg-rose-50 ring-rose-100' : 'bg-emerald-50 ring-emerald-100'}`}>
                  {overdue.count > 0 ? <AlertCircle size={20} className="text-rose-600" strokeWidth={1.75} /> : <Clock size={20} className="text-emerald-600" strokeWidth={1.75} />}
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">10-Day Invoice Follow-Up</span>
                  <h3 className="text-lg font-extrabold text-slate-900 font-[Manrope] tracking-tight">
                    {overdue.count > 0 ? `${overdue.count.toLocaleString()} ${overdue.count === 1 ? 'invoice' : 'invoices'} overdue` : 'All invoices on time'}
                  </h3>
                </div>
              </div>
              {overdue.count > 0 && (
                <div className="text-right">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">Outstanding</span>
                  <p className="text-2xl font-extrabold text-rose-700 font-[Manrope] font-mono-num tracking-tight">${overdue.total_outstanding.toLocaleString('en-NZ', { minimumFractionDigits: 2 })}</p>
                </div>
              )}
            </div>
            {overdue.count > 0 && (
              <>
                <div className="space-y-1 max-h-56 overflow-y-auto thin-scroll">
                  {overdue.items.slice(0, 5).map(inv => (
                    <div key={inv._id} onClick={() => navigate(`/invoices/${inv._id}`)} className="flex items-center justify-between gap-3 py-2 px-2 -mx-2 rounded hover:bg-rose-50/60 cursor-pointer transition-colors">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{inv.client_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500 font-mono-num truncate">{inv.invoice_number} · {inv.invoice_date}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-slate-800 font-mono-num">${(inv.total_inc_gst || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-[10px] font-semibold text-rose-600">{inv.age_days}d overdue</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-rose-100">
                  <span className="text-xs text-slate-500">Showing {Math.min(5, overdue.count)} of {overdue.count}</span>
                  <button onClick={() => navigate('/invoices')} className="text-xs text-rose-700 hover:text-rose-800 font-bold flex items-center gap-1 transition-colors">View all <ArrowRight size={12} /></button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Financial Summary */}
      {activeWidgets.includes('financial_summary') && financials && (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Financial Summary (This Month)</h3>
            <button onClick={() => navigate('/financials')} className="text-[#D35400] text-xs font-semibold flex items-center gap-1">Full Report <ArrowRight size={14} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[['Revenue', financials.total_revenue, '#D35400'], ['Profit', financials.gross_profit, '#2F855A'], ['Outstanding', financials.outstanding_amount, '#D69E2E'], ['Pipeline', financials.pipeline_value, '#4A6FA5']].map(([l, v, c]) => (
              <div key={l} className="bg-slate-50 p-3 rounded"><span className="text-xs text-slate-500">{l}</span><p className="text-lg font-bold" style={{ color: c }}>${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}</p></div>
            ))}
          </div>
        </div>
      )}

      {/* Cultural Calendar — upcoming culturally-significant dates for
          clients we have on the books. Renders when there's a date in
          the next 45 days OR an actionable nudge for a client. Per-
          celebration cards show the date + a list of "Send greeting"
          buttons for each client whose cultural_identity matches AND
          whose reminder window has opened. */}
      {activeWidgets.includes('cultural_calendar') && (culturalUpcoming.length > 0 || culturalNudges.length > 0) && (
        <div className="bg-white border border-amber-200 rounded-md p-5" data-testid="dashboard-cultural-calendar">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-amber-700 font-[Manrope]">
              Cultural Calendar — Next 45 days ({culturalUpcoming.length}) {culturalNudges.length > 0 && <span className="text-xs font-normal text-rose-600 ml-2">· {culturalNudges.length} nudge{culturalNudges.length === 1 ? '' : 's'} ready</span>}
            </h3>
            <button onClick={() => navigate('/clients')} className="text-[#D35400] text-xs font-semibold flex items-center gap-1">Clients <ArrowRight size={14} /></button>
          </div>
          {/* Active nudges — per-client one-tap buttons. Only celebrations
              whose reminder window has opened appear here. Once a greeting
              is sent / explicitly skipped the row swaps to a coloured pill
              so the office never double-greets. */}
          {culturalNudges.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="dashboard-cultural-nudges">
              {Object.entries(culturalNudges.reduce((acc, n) => {
                (acc[n.celebration_id] ||= { ...n, clients: [] }).clients.push(n);
                return acc;
              }, {})).map(([celId, group]) => (
                <div key={celId} className="bg-rose-50/60 border border-rose-200 rounded-md p-3" data-testid={`cultural-nudge-group-${celId}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-rose-800">{group.celebration_name}</span>
                    <span className="text-[10px] font-mono text-rose-600">{group.celebration_date}</span>
                    <span className="text-[10px] font-semibold text-rose-700 ml-auto">
                      {group.days_until === 0 ? 'TODAY' : `in ${group.days_until} day${group.days_until === 1 ? '' : 's'}`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {group.clients.map((n) => {
                      // Pill colour depends on action state:
                      //   sent    → emerald (✓ Sent · DD MMM)
                      //   skipped → slate  (⊝ Skipped) with re-open undo
                      //   null    → original rose "Send greeting" CTA
                      if (n.action_status === 'sent') {
                        return (
                          <span
                            key={n._id}
                            data-testid={`nudge-sent-${n._id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded ring-1 ring-emerald-300 bg-emerald-50 text-emerald-800"
                            title={`Greeting sent${n.actioned_at ? ` on ${new Date(n.actioned_at).toLocaleString()}` : ''}. Click to undo.`}
                            role="button"
                            onClick={async () => {
                              if (!window.confirm('Reinstate this nudge? The "Sent" record will be cleared so the button reappears.')) return;
                              try {
                                await api.delete(`/cultural-toolkit/nudges/${n.celebration_id}/${n.client_id}?year=${n.year}`);
                                load();
                              } catch (e) { /* surface silently */ }
                            }}
                          >
                            ✓ Sent {n.client_name || n.client_email}{n.actioned_at ? ` · ${new Date(n.actioned_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}` : ''}
                          </span>
                        );
                      }
                      if (n.action_status === 'skipped') {
                        return (
                          <span
                            key={n._id}
                            data-testid={`nudge-skipped-${n._id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded ring-1 ring-slate-300 bg-slate-50 text-slate-600 line-through"
                            title="Skipped for this year. Click to reinstate."
                            role="button"
                            onClick={async () => {
                              try {
                                await api.delete(`/cultural-toolkit/nudges/${n.celebration_id}/${n.client_id}?year=${n.year}`);
                                load();
                              } catch (e) { /* silent */ }
                            }}
                          >
                            ⊝ Skipped · {n.client_name || n.client_email}
                          </span>
                        );
                      }
                      return (
                        <span key={n._id} className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            data-testid={`nudge-send-${n._id}`}
                            onClick={() => setGreetingModal(n)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded ring-1 ring-rose-300 bg-white hover:bg-rose-100 text-rose-800"
                            title={`Send a ${group.celebration_name} greeting to ${n.client_name}`}
                          >
                            📧 {n.client_name || n.client_email}
                          </button>
                          <button
                            type="button"
                            data-testid={`nudge-skip-${n._id}`}
                            onClick={async () => {
                              try {
                                await api.post(`/cultural-toolkit/nudges/${n.celebration_id}/${n.client_id}/skip`, { year: n.year });
                                load();
                              } catch (e) { /* silent */ }
                            }}
                            className="text-[10px] text-slate-400 hover:text-slate-700 underline-offset-2 hover:underline"
                            title="Skip this client for this year — pill goes grey and won't bug you again until next year"
                          >
                            skip
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Read-only horizon view — what's coming up beyond the active
              reminder windows. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {culturalUpcoming.slice(0, 9).map((item) => {
              const isSoon = item.days_until <= item.reminder_days_before;
              return (
                <div
                  key={item._id}
                  className={`p-2.5 rounded border ${isSoon ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-100'}`}
                  data-testid={`cultural-calendar-item-${item._id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-800 flex-1 truncate">{item.name}</span>
                    <span className={`text-[10px] font-bold ${isSoon ? 'text-rose-700' : 'text-amber-700'}`}>
                      {item.days_until === 0 ? 'TODAY' : `${item.days_until}d`}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center justify-between mt-0.5">
                    <span>{item.culture_key} · {item.client_count} client{item.client_count === 1 ? '' : 's'}</span>
                    <span className="font-mono">{item.next_date}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Greeting modal — re-uses EmailModal pre-populated with the
          nudge's subject + body + client email. No PDF attached
          (greetings are short, friendly notes). On a successful send
          we POST to /mark-sent so the button swaps to a ✓ Sent pill. */}
      <EmailModal
        isOpen={!!greetingModal}
        onClose={() => setGreetingModal(null)}
        defaultTo={greetingModal?.client_email || ''}
        defaultSubject={greetingModal?.subject || ''}
        defaultBody={greetingModal?.body || ''}
        appraisalId=""
        defaultAttachPdf={false}
        markQuoteSent={false}
        onSent={async () => {
          if (!greetingModal) return;
          try {
            await api.post(
              `/cultural-toolkit/nudges/${greetingModal.celebration_id}/${greetingModal.client_id}/mark-sent`,
              { year: greetingModal.year },
            );
          } catch (e) { /* silent — UI will just not flip until reload */ }
          await load();
        }}
      />

      {/* Low Stock Alerts */}
      {activeWidgets.includes('low_stock') && lowStock.length > 0 && (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-red-700 font-[Manrope]">Low Stock Alerts ({lowStock.length})</h3>
            <button onClick={() => navigate('/inventory')} className="text-[#D35400] text-xs font-semibold flex items-center gap-1">Inventory <ArrowRight size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {lowStock.slice(0, 6).map(item => (
              <div key={item._id} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-100">
                <span className="text-sm text-slate-800">{item.name}</span>
                <span className="text-xs font-bold text-red-600">{item.quantity_in_stock} / {item.reorder_level}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {activeWidgets.includes('recent_activity') && auditLog.length > 0 && (
        <div className="bg-white border border-[#E2E2DF] rounded-md p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Recent Activity</h3>
            <button onClick={() => navigate('/audit-log')} className="text-[#D35400] text-xs font-semibold flex items-center gap-1">View All <ArrowRight size={14} /></button>
          </div>
          <div className="space-y-2">
            {auditLog.slice(0, 6).map(a => (
              <div key={a._id} className="flex items-center justify-between py-1.5 border-b border-[#E2E2DF] last:border-0">
                <div><span className="text-xs font-semibold text-slate-700">{(a.action || '').replace(/_/g, ' ')}</span><span className="text-xs text-slate-400 ml-2">{a.entity_type}</span></div>
                <div className="text-right"><span className="text-[10px] text-slate-400">{a.user_name || 'System'}</span>{a.created_at && <p className="text-[10px] text-slate-300">{new Date(a.created_at).toLocaleString()}</p>}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeWidgets.includes('marketing_recap') && <MarketingRecapCard />}

      {/* Customize Modal */}
      {showCustomize && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCustomize(false)}>
          <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#E2E2DF]">
              <h2 className="text-lg font-bold text-slate-900 font-[Manrope]">Customize Dashboard</h2>
              <button onClick={() => setShowCustomize(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-2">
              <p className="text-xs text-slate-500 mb-3">Toggle widgets on/off. Drag to reorder.</p>
              {ALL_WIDGETS.map((w, idx) => {
                const active = tempWidgets.includes(w.id);
                return (
                  <div key={w.id} draggable onDragStart={e => e.dataTransfer.setData('idx', idx.toString())} onDragOver={e => e.preventDefault()} onDrop={e => { const from = parseInt(e.dataTransfer.getData('idx')); moveWidget(from, idx); }} className={`flex items-center gap-3 p-3 rounded-md border transition-all cursor-move ${active ? 'border-[#D35400] bg-[#D35400]/5' : 'border-[#E2E2DF]'}`}>
                    <GripVertical size={14} className="text-slate-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800">{w.label}</p><p className="text-[10px] text-slate-400">{w.desc}</p></div>
                    <button onClick={() => toggleWidget(w.id)} className={`p-1.5 rounded ${active ? 'text-[#D35400]' : 'text-slate-300'}`}>{active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                  </div>
                );
              })}
            </div>
            <div className="p-5 border-t border-[#E2E2DF] flex justify-end gap-3">
              <button onClick={() => setShowCustomize(false)} className="px-4 py-2 border border-[#E2E2DF] rounded-md text-sm hover:bg-slate-50">Cancel</button>
              <button data-testid="save-dashboard-prefs" onClick={() => savePrefs(tempWidgets)} className="px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold">Save Layout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
